/**
 * Admin module constants - categories, thicknesses, sizes, and configurations
 */

// Product categories
export const CATEGORIES = ['Plywood', 'Blockboard', 'Laminates', 'Highlighters', 'Veneers', 'Wooden Flooring', 'Hardware', 'Screws/Patti', 'ACP', 'Adhesives', 'Doors', 'WPC/PVC', 'MDF'];

// Rate types for pricing
// Master list of rate units used across every product category.
// Default per category lives in DEFAULT_RATE_TYPE_BY_CATEGORY below.
export const RATE_TYPES = [
  'per piece',
  'per pair',
  'per dozen',
  'per set',
  'per box',
  'per bottle',
  'per roll',
  'per bundle',
  'per bag',
  'per sheet',
  'per sq ft',
  'per running feet',
  'per foot',
  'per meter',
  'per kg',
  'per gram',
  'per liter',
  'per ml',
];

// Per-category sensible default if not specified
export const DEFAULT_RATE_TYPE_BY_CATEGORY = {
  Plywood: 'per sq ft',
  Blockboard: 'per sq ft',
  MDF: 'per sq ft',
  'WPC/PVC': 'per sq ft',
  ACP: 'per sq ft',
  Doors: 'per piece',
  Laminates: 'per sheet',
  Highlighters: 'per sheet',
  Veneers: 'per sq ft',
  'Wooden Flooring': 'per box',
  Hardware: 'per piece',
  'Screws/Patti': 'per piece',
  Adhesives: 'per kg',
};

// Categories that use multi-thickness bulk add mode
export const THICKNESS_BULK_CATEGORIES = ['Plywood', 'Blockboard', 'Doors', 'WPC/PVC', 'MDF', 'ACP'];

// Categories that use weight bulk mode
export const WEIGHT_BULK_CATEGORIES = ['Adhesives'];

// Categories that use veneer/laminate code-based mode
export const CODE_BASED_CATEGORIES = ['Laminates', 'Highlighters', 'Veneers', 'ACP', 'Wooden Flooring'];

// Veneer types
export const VENEER_TYPES = ['Hybrid', 'Natural'];

// Veneer sizes
export const VENEER_SIZES = [
  { id: '8x4', label: '8x4 ft' },
  { id: '10x4', label: '10x4 ft' },
];

// Hardware types and their configurations
export const HARDWARE_TYPES = [
  { id: 'screw', label: 'Screw', sizeUnit: 'mm', rateOptions: ['per box', 'per dozen', 'per piece'], hasBoxQty: true },
  { id: 'nail', label: 'Nail', sizeUnit: 'inch', rateOptions: ['per kg', 'per box'], hasBoxQty: true },
  { id: 'lipping', label: 'Lipping/Patti', sizeUnit: 'inch', rateOptions: ['per running feet'], hasBoxQty: false },
  { id: 'fastener', label: 'Fastener/Passner', sizeUnit: 'inch', rateOptions: ['per dozen', 'per piece'], hasBoxQty: true },
  { id: 'moulding', label: 'Moulding', sizeUnit: 'inch', rateOptions: ['per running feet'], hasBoxQty: false },
];

// Hardware subcategory templates
export const HARDWARE_SUBCATEGORIES = {
  screw: ['Wood Screw', 'Pop Black Screw', 'OMNI/CSK Screw', 'OMNI/Star SS Screw', 'SS Screw', 'GI Screw'],
  nail: ['Wire Nail', 'Concrete Nail', 'Finishing Nail'],
  lipping: ['Lipping BTC 50:50', 'Lipping BTC 6\' & Above', 'Lipping REG', 'Lipping 2ND'],
  fastener: ['Passner/Fastener'],
  moulding: ['H/R Moulding', 'Quarter Round', 'Scotia'],
};

// Plywood specific constants
export const PLYWOOD_THICKNESSES = [
  { id: '4MM', label: '4 MM' },
  { id: '6MM', label: '6 MM' },
  { id: '8MM', label: '8 MM' },
  { id: '9MM', label: '9 MM' },
  { id: '12MM', label: '12 MM' },
  { id: '15MM', label: '15 MM' },
  { id: '16MM', label: '16 MM' },
  { id: '18MM', label: '18 MM' },
  { id: '19MM', label: '19 MM' },
];

// Blockboard specific thicknesses
export const BLOCKBOARD_THICKNESSES = [
  { id: '18MM', label: '18 MM' },
  { id: '19MM', label: '19 MM' },
  { id: '25MM', label: '25 MM' },
  { id: '30MM', label: '30 MM' },
  { id: '32MM', label: '32 MM' },
  { id: '42MM', label: '42 MM' },
];

// MDF thicknesses
export const MDF_THICKNESSES = [
  { id: '2.5MM', label: '2.5 MM' },
  { id: '3MM', label: '3 MM' },
  { id: '6MM', label: '6 MM' },
  { id: '8MM', label: '8 MM' },
  { id: '12MM', label: '12 MM' },
  { id: '18MM', label: '18 MM' },
  { id: '25MM', label: '25 MM' },
];

// Doors thicknesses
export const DOORS_THICKNESSES = [
  { id: '25MM', label: '25 MM' },
  { id: '30MM', label: '30 MM' },
  { id: '32MM', label: '32 MM' },
  { id: '35MM', label: '35 MM' },
  { id: '40MM', label: '40 MM' },
  { id: '42MM', label: '42 MM' },
  { id: '45MM', label: '45 MM' },
  { id: '60MM', label: '60 MM' },
];

// Door sizes in inches (height x width)
export const DOOR_SIZES = [
  '81x28', '81x32', '84x30', '84x36', '84x39', '84x42', '84x48',
  '96x30', '96x32', '96x36', '96x39', '96x42', '96x48'
];

// WPC/PVC thicknesses
export const WPCPVC_THICKNESSES = [
  { id: '5MM', label: '5 MM' },
  { id: '8MM', label: '8 MM' },
  { id: '10MM', label: '10 MM' },
  { id: '12MM', label: '12 MM' },
  { id: '15MM', label: '15 MM' },
  { id: '18MM', label: '18 MM' },
];

// Veneers thicknesses
export const VENEERS_THICKNESSES = [
  { id: '3MM', label: '3 MM' },
  { id: '4MM', label: '4 MM' },
  { id: '6MM', label: '6 MM' },
];

// ACP thicknesses
export const ACP_THICKNESSES = [
  { id: '3MM', label: '3 MM' },
  { id: '4MM', label: '4 MM' },
];

// Standard plywood sizes
export const PLYWOOD_SIZES = [
  { id: '10x4', label: '10 x 4', sqft: 40 },
  { id: '9x4', label: '9 x 4', sqft: 36 },
  { id: '8x4', label: '8 x 4', sqft: 32 },
  { id: '8x3', label: '8 x 3', sqft: 24 },
  { id: '7x4', label: '7 x 4', sqft: 28 },
  { id: '7x3', label: '7 x 3', sqft: 21 },
  { id: '6x4', label: '6 x 4', sqft: 24 },
  { id: '6x3', label: '6 x 3', sqft: 18 },
];

// Door sizes in inches with sqft calculation
export const DOOR_SIZES_WITH_SQFT = [
  { id: '81x28', label: '81" x 28"', sqft: (81 * 28) / 144 },
  { id: '81x32', label: '81" x 32"', sqft: (81 * 32) / 144 },
  { id: '84x30', label: '84" x 30"', sqft: (84 * 30) / 144 },
  { id: '84x36', label: '84" x 36"', sqft: (84 * 36) / 144 },
  { id: '84x39', label: '84" x 39"', sqft: (84 * 39) / 144 },
  { id: '84x42', label: '84" x 42"', sqft: (84 * 42) / 144 },
  { id: '84x48', label: '84" x 48"', sqft: (84 * 48) / 144 },
  { id: '96x30', label: '96" x 30"', sqft: (96 * 30) / 144 },
  { id: '96x32', label: '96" x 32"', sqft: (96 * 32) / 144 },
  { id: '96x36', label: '96" x 36"', sqft: (96 * 36) / 144 },
  { id: '96x39', label: '96" x 39"', sqft: (96 * 39) / 144 },
  { id: '96x42', label: '96" x 42"', sqft: (96 * 42) / 144 },
  { id: '96x48', label: '96" x 48"', sqft: (96 * 48) / 144 },
];

// Adhesive weights
export const ADHESIVE_WEIGHTS = [
  { id: '1KG', label: '1 KG' },
  { id: '2KG', label: '2 KG' },
  { id: '5KG', label: '5 KG' },
  { id: '10KG', label: '10 KG' },
  { id: '20KG', label: '20 KG' },
  { id: '30KG', label: '30 KG' },
  { id: '50KG', label: '50 KG' },
  { id: '60KG', label: '60 KG' },
];

// Adhesive volumes (liters)
export const ADHESIVE_VOLUMES = [
  { id: '0.5LTR', label: '0.5 LTR' },
  { id: '1LTR', label: '1 LTR' },
  { id: '2LTR', label: '2 LTR' },
  { id: '5LTR', label: '5 LTR' },
  { id: '10LTR', label: '10 LTR' },
  { id: '20LTR', label: '20 LTR' },
];

// Empty product template
export const emptyProduct = {
  brand_name: '', 
  main_category: '', 
  subcategory: '', 
  product_name: '', 
  product_code: '',
  thickness: '', 
  size: '', 
  grade: '', 
  finish: '', 
  rate_type: 'per sheet', 
  size_unit: 'feet', 
  base_rate: 0,
  gst_included: true, 
  image_url: '', 
  status: 'active', 
  available_sizes: [], 
  codes_and_rates: [],
  brand_description: '',  // Shown in PDF for Plywood, Blockboard, etc.
  show_brand_in_pdf: true,  // Per-product brand visibility in customer PDF (Admin only)
  // Dual-rate support (currently used by Adhesives + Wooden Flooring).
  // Leave at 0/'' to hide the rate-chip selector in the cart.
  secondary_base_rate: 0,
  secondary_rate_type: '',
  // Wooden Flooring: sq ft per sealed box (e.g. 22.5). Drives the
  // box ↔ sq-ft conversion in the cart + the "1 box = X sq ft" PDF line.
  sqft_per_box: 0,
};

// Helper function to get thicknesses for a category
export function getThicknessesForCategory(category) {
  switch (category) {
    case 'Plywood': return PLYWOOD_THICKNESSES;
    case 'Blockboard': return BLOCKBOARD_THICKNESSES;
    case 'MDF': return MDF_THICKNESSES;
    case 'Doors': return DOORS_THICKNESSES;
    case 'WPC/PVC': return WPCPVC_THICKNESSES;
    case 'Veneers': return VENEERS_THICKNESSES;
    case 'ACP': return ACP_THICKNESSES;
    default: return PLYWOOD_THICKNESSES;
  }
}

// Helper function to get sizes for a category
export function getSizesForCategory(category) {
  if (category === 'Doors') {
    return DOOR_SIZES_WITH_SQFT;
  }
  return PLYWOOD_SIZES;
}
